import React, { useState, useEffect, useRef, FunctionComponent } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { FaSearch, FaTimes, FaClock, FaUtensils, FaThumbsUp, FaThumbsDown, FaHeart } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';

const FaSearchI = FaSearch as FunctionComponent<{ className?: string }>;
const FaTimesI = FaTimes as FunctionComponent<{ className?: string }>;
const FaClockI = FaClock as FunctionComponent<{ className?: string }>;
const FaUtensilsI = FaUtensils as FunctionComponent<{ className?: string }>;
const FaThumbsUpI = FaThumbsUp as FunctionComponent<{ className?: string }>;
const FaThumbsDownI = FaThumbsDown as FunctionComponent<{ className?: string }>;
const FaHeartI = FaHeart as FunctionComponent<{ className?: string }>;

interface Nutrient { id?: number; nutrient_name?: string; value?: string; }
interface ReplaceableIngredient {
  id?: number;
  Comment?: string | null;
  IngredientName?: { id: number; name?: string; image?: RecipeImage } | string | null;
}
interface Ingredient {
  id?: number;
  IngredientName?: string;
  Quantity?: string;
  Unit?: string;
  Comments?: string;
  IsReplaceable?: boolean;
  replaceableIngredients?: ReplaceableIngredient[];
}
interface RecipeCategory { id: number; documentId?: string; recipe_category_name: string; slug?: string; }
interface RecipeImage { url?: string; formats?: { thumbnail?: { url: string }; small?: { url: string }; medium?: { url: string }; large?: { url: string } }; }
interface Recipe {
  id: number;
  documentId?: string;
  title: string;
  description?: string;
  cooking_time?: string;
  servings?: number;
  image?: RecipeImage;
  recipe_category?: RecipeCategory;
  nutrients?: Nutrient[];
  ingredients?: Ingredient[];
  instructions?: any;
  thumbs_up_count?: number;
  thumbs_down_count?: number;
  is_shared?: boolean;
  user?: { id: number; username?: string; profile_picture?: RecipeImage };
  createdAt?: string;
}

const PAGE_SIZE = 24;

// Scroll-triggered reveal
const useInView = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold, rootMargin: '0px 0px -30px 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const resolveImageUrl = (img?: RecipeImage): string | undefined => {
  if (!img) return undefined;
  const url = img.formats?.medium?.url || img.formats?.small?.url || img.formats?.thumbnail?.url || img.url;
  if (!url) return undefined;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// Render Strapi blocks (instructions) as plain paragraphs
const renderBlocks = (blocks: any): React.ReactNode => {
  if (!blocks) return null;
  if (typeof blocks === 'string') return <p className="text-sm text-gray-700 leading-relaxed mb-2">{blocks}</p>;
  if (!Array.isArray(blocks)) return null;
  return blocks.map((block: any, i: number) => {
    if (block.type === 'paragraph') {
      const text = (block.children || []).map((c: any) => c.text || '').join('');
      if (!text.trim()) return null;
      return <p key={i} className="text-sm text-gray-700 leading-relaxed mb-2">{text}</p>;
    }
    if (block.type === 'list') {
      const items = (block.children || []).map((li: any, li_i: number) => {
        const text = (li.children || []).map((c: any) => c.text || '').join('');
        return <li key={li_i} className="text-sm text-gray-700 leading-relaxed">{text}</li>;
      });
      return block.format === 'ordered'
        ? <ol key={i} className="list-decimal list-inside space-y-1 mb-2">{items}</ol>
        : <ul key={i} className="list-disc list-inside space-y-1 mb-2">{items}</ul>;
    }
    if (block.type === 'heading') {
      const text = (block.children || []).map((c: any) => c.text || '').join('');
      return <h4 key={i} className="text-base font-bold text-gray-900 mt-3 mb-1">{text}</h4>;
    }
    return null;
  });
};

interface Props { isLoggedIn?: boolean; isPremium?: boolean; }

type TabId = 'my' | 'favorites' | 'saved';

/* ═══ Logged-out marketing view with advanced animations ═══ */
const LoggedOutRecipesView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'my' | 'favorites' | 'saved'>('my');

  // Auto-cycle the demo tabs
  useEffect(() => {
    const t = setInterval(() => {
      setActiveTab((prev) => prev === 'my' ? 'favorites' : prev === 'favorites' ? 'saved' : 'my');
    }, 3500);
    return () => clearInterval(t);
  }, []);

  const demoRecipes: Record<string, { n: string; tag: string; diet: string; time: string; emoji: string; protein: number }[]> = {
    my: [
      { n: 'My Ribeye Special', tag: 'Beef', diet: 'Carnivore', time: '20m', emoji: '🥩', protein: 68 },
      { n: 'Bacon-Egg Cups', tag: 'Eggs', diet: 'Carnivore', time: '15m', emoji: '🍳', protein: 42 },
      { n: 'Keto Chicken Bowl', tag: 'Poultry', diet: 'Ketovore', time: '18m', emoji: '🥑', protein: 55 },
    ],
    favorites: [
      { n: 'Wagyu Tartare', tag: 'Beef', diet: 'Carnivore', time: '10m', emoji: '🥩', protein: 48 },
      { n: 'Avocado-Bacon Bowl', tag: 'Low-carb', diet: 'Ketovore', time: '10m', emoji: '🥑', protein: 32 },
      { n: 'Beef Bone Broth', tag: 'Broth', diet: 'Carnivore', time: '8h', emoji: '🦴', protein: 18 },
    ],
    saved: [
      { n: 'Custom Pork Belly', tag: 'Pork', diet: 'Carnivore', time: '1h', emoji: '🥓', protein: 45 },
      { n: 'My Caesar (no croutons)', tag: 'Low-carb', diet: 'Ketovore', time: '15m', emoji: '🥗', protein: 38 },
      { n: 'Lamb Chops Rosemary', tag: 'Lamb', diet: 'Carnivore', time: '25m', emoji: '🍖', protein: 62 },
    ],
  };

  const tabConfig = {
    my: { emoji: '🍳', label: 'My Recipes', color: '#f97316', desc: 'Create unlimited recipes in the app (free)' },
    favorites: { emoji: '❤️', label: 'Favorites', color: '#ec4899', desc: 'Heart recipes from the Premium library' },
    saved: { emoji: '🔖', label: 'Customized', color: '#8b5cf6', desc: 'Premium recipes tweaked to your taste' },
  };

  const current = tabConfig[activeTab];
  const list = demoRecipes[activeTab];

  return (
    <div className="relative overflow-hidden">
      <style>{`
        @keyframes floatRecipe { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-10px) rotate(0deg); } }
        @keyframes floatRecipe2 { 0%,100% { transform: translateY(0) rotate(2deg); } 50% { transform: translateY(-12px) rotate(-1deg); } }
        @keyframes floatRecipe3 { 0%,100% { transform: translateY(0) rotate(-1deg); } 50% { transform: translateY(-8px) rotate(3deg); } }
        @keyframes pulseBlob { 0%,100% { opacity: 0.25; } 50% { opacity: 0.5; } }
        @keyframes cardIn { 0% { opacity: 0; transform: translateX(20px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes shimmerText { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes rotate360 { to { transform: rotate(360deg); } }
        @keyframes heartBeat { 0%,100% { transform: scale(1); } 25%,75% { transform: scale(1.15); } 50% { transform: scale(0.95); } }
        @keyframes ping-slow { 75%,100% { transform: scale(2); opacity: 0; } }
        .shimmer-rx { background: linear-gradient(90deg, #f97316 0%, #ef4444 30%, #f97316 60%, #ef4444 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: shimmerText 6s linear infinite; }
      `}</style>

      {/* Ambient pulsing blobs */}
      <div className="pointer-events-none absolute -top-24 -left-20 w-96 h-96 bg-red-200 rounded-full blur-3xl" style={{ animation: 'pulseBlob 9s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute top-40 -right-20 w-[28rem] h-[28rem] bg-orange-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulseBlob 11s ease-in-out infinite 1s' }} />
      <div className="pointer-events-none absolute bottom-20 left-1/4 w-80 h-80 bg-purple-200 rounded-full blur-3xl opacity-25" style={{ animation: 'pulseBlob 13s ease-in-out infinite 2s' }} />

      {/* ── Main hero with animated preview ── */}
      <section className="relative max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          {/* LEFT — copy */}
          <div style={{ animation: 'cardIn 0.8s ease-out' }}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full px-3 py-1 text-xs font-bold mb-5 shadow-md shadow-amber-500/20">
              <span className="relative w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-white" style={{ animation: 'ping-slow 2s ease-in-out infinite' }} />
                <span className="relative w-2 h-2 rounded-full bg-white block" />
              </span>
              Recipes · in the EatMeat app
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tight leading-[1.05] mb-5">
              Create <span className="shimmer-rx">unlimited</span> recipes <br /> on iOS &amp; Android
            </h1>
            <p className="text-lg text-gray-500 mb-8 max-w-md">
              Build your own carnivore &amp; ketovore recipes in the app — unlimited, free. Unlock 5,000+ curated Premium recipes with macros, cooking times, and one-tap ingredient swaps.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href="https://apps.apple.com/app/id6746769046" target="_blank" rel="noreferrer"
                className="group relative overflow-hidden px-7 py-3.5 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
                <span className="text-lg">🍎</span>
                <span>App Store</span>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat" target="_blank" rel="noreferrer"
                className="group relative overflow-hidden px-7 py-3.5 bg-green-600 text-white rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
                <span className="text-lg">🤖</span>
                <span>Play Store</span>
              </a>
            </div>

            <div className="text-xs text-gray-500 mb-6">
              Already a subscriber?{' '}
              <a href="/login" className="text-primary font-semibold hover:underline">Log in</a> to access the library on the web.
            </div>

            {/* Value props */}
            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { icon: '🍳', n: '5,000+', l: 'Recipes' },
                { icon: '❤️', n: 'Favorite', l: 'For 1-tap access' },
                { icon: '✏️', n: 'Customize', l: 'Swap ingredients' },
              ].map((v, i) => (
                <div key={i} className="text-center p-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" style={{ animation: `cardIn 0.6s ease-out ${0.2 + i * 0.08}s backwards` }}>
                  <div className="text-2xl mb-1">{v.icon}</div>
                  <div className="text-lg font-extrabold text-gray-900">{v.n}</div>
                  <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{v.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — animated recipe stack preview */}
          <div className="relative min-h-[480px] flex items-center justify-center" style={{ animation: 'cardIn 0.9s ease-out 0.1s backwards' }}>
            {/* Orbiting decorative ring */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[110%] h-[110%] rounded-full opacity-40" style={{
                background: 'conic-gradient(from 0deg, transparent 70%, rgba(249,115,22,0.25) 85%, transparent 100%)',
                animation: 'rotate360 20s linear infinite',
              }} />
            </div>

            {/* Tab pill switcher on top */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
              <div className="inline-flex bg-white rounded-full p-1 shadow-lg border border-gray-100">
                {(Object.keys(tabConfig) as Array<keyof typeof tabConfig>).map((key) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-all flex items-center gap-1.5 ${
                      activeTab === key ? 'text-white shadow-md' : 'text-gray-500 hover:text-gray-700'
                    }`}
                    style={activeTab === key ? { background: `linear-gradient(135deg, ${tabConfig[key].color}, ${tabConfig[key].color}dd)` } : {}}
                  >
                    <span>{tabConfig[key].emoji}</span>
                    <span className="hidden sm:inline">{tabConfig[key].label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Floating decorative badges */}
            <div className="absolute top-16 -left-2 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-3 py-2 flex items-center gap-2 text-xs" style={{ animation: 'floatRecipe 5s ease-in-out infinite' }}>
              <div className="w-8 h-8 rounded-xl bg-red-50 flex items-center justify-center text-lg" style={{ animation: 'heartBeat 1.5s ease-in-out infinite' }}>❤</div>
              <div>
                <div className="text-[9px] text-gray-400 font-semibold uppercase">Favorited</div>
                <div className="font-bold text-gray-800">12.4k</div>
              </div>
            </div>
            <div className="absolute bottom-20 -right-2 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-3 py-2 flex items-center gap-2 text-xs" style={{ animation: 'floatRecipe2 6s ease-in-out infinite' }}>
              <span className="text-xl">🥩</span>
              <div>
                <div className="text-[9px] text-gray-400 font-semibold uppercase">Protein</div>
                <div className="font-bold text-gray-800">68g avg</div>
              </div>
            </div>
            {/* Premium badge — gradient, prominent */}
            <div className="absolute top-24 -right-3 z-10 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-2xl shadow-xl px-3 py-2 flex items-center gap-2 text-xs" style={{ animation: 'floatRecipe3 7s ease-in-out infinite' }}>
              <div className="text-xl">★</div>
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider opacity-90">Premium</div>
                <div className="text-[11px] font-extrabold">Members only</div>
              </div>
            </div>

            {/* Main card stack */}
            <div className="relative" style={{ width: 320 }}>
              {/* Background cards */}
              <div className="absolute inset-0 translate-x-4 translate-y-4 bg-white rounded-3xl border border-gray-100 shadow-lg opacity-50" />
              <div className="absolute inset-0 translate-x-8 translate-y-8 bg-white rounded-3xl border border-gray-100 shadow-md opacity-25" />

              {/* Foreground card */}
              <div
                key={activeTab}
                className="relative bg-white rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/60 overflow-hidden"
                style={{ animation: 'cardIn 0.5s ease-out' }}
              >
                {/* Card header */}
                <div className="relative h-28 overflow-hidden" style={{ background: `linear-gradient(135deg, ${current.color}, ${current.color}aa)` }}>
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, white 0%, transparent 50%)' }} />
                  <div className="relative p-4 flex items-center justify-between h-full">
                    <div className="text-white">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{current.emoji}</span>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Your Collection</div>
                          <div className="text-lg font-extrabold leading-tight">{current.label}</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-white/80 mt-1">{current.desc}</div>
                    </div>
                    <div className="bg-white/20 backdrop-blur rounded-full px-2 py-1 text-[10px] font-bold text-white">{list.length} items</div>
                  </div>
                </div>

                {/* Recipe rows */}
                <div className="p-3 space-y-2">
                  {list.map((r, i) => {
                    const isKeto = r.diet === 'Ketovore';
                    return (
                      <div
                        key={`${activeTab}-${i}`}
                        className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors"
                        style={{ animation: `cardIn 0.4s ease-out ${0.1 + i * 0.08}s backwards` }}
                      >
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${isKeto ? 'bg-gradient-to-br from-green-100 to-emerald-100' : 'bg-gradient-to-br from-orange-100 to-red-100'}`}>
                          {r.emoji}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm text-gray-800 truncate">{r.n}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-[9px] font-bold text-white px-1.5 py-0.5 rounded-full ${isKeto ? 'bg-green-500' : 'bg-red-500'}`}>
                              {isKeto ? '🥑' : '🥩'} {r.diet}
                            </span>
                            <span className="text-[10px] text-gray-400">⏱ {r.time}</span>
                            <span className="text-[10px] text-red-500 font-bold">🥩 {r.protein}g</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-gray-300 text-xl" style={activeTab === 'favorites' ? { color: '#ec4899', animation: 'heartBeat 1.5s ease-in-out infinite' } : {}}>
                          {activeTab === 'my' ? '✏️' : activeTab === 'favorites' ? '❤' : '🔖'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Sync indicator footer */}
                <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="relative w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />
                      <span className="relative w-2 h-2 rounded-full bg-green-500 block" />
                    </span>
                    <span className="text-[10px] font-semibold text-gray-600">Synced to cloud</span>
                  </div>
                  <span className="text-[10px] text-gray-400">Just now</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── What you can do section ── */}
      <section className="relative max-w-6xl mx-auto px-4 md:px-6 pb-16 md:pb-20">
        <div className="text-center mb-10" style={{ animation: 'cardIn 0.7s ease-out' }}>
          <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">How it works</div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">Three ways to collect recipes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              key: 'create',
              emoji: '🍳',
              title: 'Create unlimited',
              tag: 'Free in the app',
              desc: 'Build as many of your own carnivore or ketovore recipes as you want — with ingredients, instructions, and auto-calculated macros. No caps.',
              color: 'from-orange-500 to-red-500',
              bg: 'bg-orange-50',
              text: 'text-orange-600',
            },
            {
              key: 'favorite',
              emoji: '❤️',
              title: '5,000+ recipes',
              tag: 'Premium',
              desc: 'Browse a curated library of carnivore and ketovore recipes. Heart your go-tos for one-tap access from your dashboard.',
              color: 'from-pink-500 to-rose-500',
              bg: 'bg-pink-50',
              text: 'text-pink-600',
            },
            {
              key: 'save',
              emoji: '✏️',
              title: 'Customize',
              tag: 'Premium',
              desc: 'Tap any ingredient on a Premium recipe to swap in an alternative that fits your diet — saved to your account, synced across devices.',
              color: 'from-purple-500 to-violet-500',
              bg: 'bg-purple-50',
              text: 'text-purple-600',
            },
          ].map((step, i) => (
            <div
              key={step.key}
              className="group relative bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              style={{ animation: `cardIn 0.7s ease-out ${0.1 + i * 0.1}s backwards` }}
            >
              {/* Step number */}
              <div className={`absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-50 ${step.text} text-xs font-bold flex items-center justify-center group-hover:scale-110 transition-transform`}>
                0{i + 1}
              </div>

              {/* Icon */}
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center text-2xl mb-4 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300`}>
                {step.emoji}
              </div>

              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-extrabold text-gray-900">{step.title}</h3>
                {step.tag === 'Premium' ? (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">★ {step.tag}</span>
                ) : (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">{step.tag}</span>
                )}
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>

              {/* Hover shine */}
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent group-hover:translate-x-[400%] transition-transform duration-1000" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Big CTA ── */}
      <section className="relative bg-white border-t border-gray-100">
        <div className="relative max-w-4xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 rounded-full px-3 py-1 text-xs font-semibold mb-5" style={{ animation: 'cardIn 0.6s ease-out' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" /> Get the EatMeat app
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight mb-4" style={{ animation: 'cardIn 0.7s ease-out 0.1s backwards' }}>
            Build your <span className="shimmer-rx">recipe collection</span> <br /> on iOS &amp; Android
          </h2>
          <p className="text-gray-500 mb-8 max-w-md mx-auto" style={{ animation: 'cardIn 0.7s ease-out 0.2s backwards' }}>
            Free to create your own carnivore &amp; ketovore recipes. Unlock the 5,000+ curated library with EatMeat Premium.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" style={{ animation: 'cardIn 0.7s ease-out 0.3s backwards' }}>
            <a href="https://apps.apple.com/app/id6746769046" target="_blank" rel="noreferrer"
              className="group px-7 py-3.5 bg-gray-900 text-white rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
              <span className="text-lg">🍎</span> Download for iOS
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat" target="_blank" rel="noreferrer"
              className="group px-7 py-3.5 bg-green-600 text-white rounded-xl font-bold shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
              <span className="text-lg">🤖</span> Download for Android
            </a>
          </div>
          <div className="text-xs text-gray-500 mt-4" style={{ animation: 'cardIn 0.7s ease-out 0.4s backwards' }}>
            Already a Premium subscriber?{' '}
            <a href="/login" className="text-primary font-semibold hover:underline">Log in</a> to browse on the web.
          </div>

          {/* Trust row */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-10 text-xs text-gray-500" style={{ animation: 'cardIn 0.7s ease-out 0.4s backwards' }}>
            {['✓ Free to create your own', '✓ 5,000+ Premium recipes', '✓ Macro tracking', '✓ Cloud sync'].map((t, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="font-semibold">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* ═══ Logged-in but not premium — paywall ═══ */
const PremiumPaywallView: React.FC = () => {
  return (
    <div className="relative min-h-[70vh] overflow-hidden">
      <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-red-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 9s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute top-20 -right-20 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-25" style={{ animation: 'pulse-slow 11s ease-in-out infinite 1s' }} />

      <div className="relative max-w-3xl mx-auto px-6 py-20 md:py-28 text-center">
        <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-semibold text-amber-700 mb-6 shadow-sm">
          <span>★</span> Premium feature
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-tight mb-4">
          Unlock the full <span className="text-primary">recipe library</span>
        </h1>
        <p className="text-lg text-gray-500 max-w-xl mx-auto mb-8">
          Browse thousands of carnivore & ketovore recipes, customize ingredients to your taste, and save favorites — all available with EatMeat Premium.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-8">
          <a href="https://apps.apple.com/app/id6746769046" target="_blank" rel="noreferrer"
            className="px-7 py-3.5 bg-gray-900 text-white rounded-xl font-semibold shadow-lg hover:-translate-y-0.5 transition-all">
            🍎 Get on App Store
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat" target="_blank" rel="noreferrer"
            className="px-7 py-3.5 bg-green-600 text-white rounded-xl font-semibold shadow-lg hover:-translate-y-0.5 transition-all">
            🤖 Get on Play Store
          </a>
        </div>
        <p className="text-xs text-gray-400">
          Already subscribed? Restore in the mobile app to sync your Premium access here.
        </p>

        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto opacity-90">
          {[
            { icon: '🥩', label: 'Macros' },
            { icon: '⏱', label: 'Cooking time' },
            { icon: '✏️', label: 'Customize' },
            { icon: '❤️', label: 'Favorites' },
          ].map((f) => (
            <div key={f.label} className="bg-white rounded-xl border border-gray-100 px-4 py-4 shadow-sm">
              <div className="text-2xl">{f.icon}</div>
              <div className="text-xs font-semibold text-gray-700 mt-1">{f.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Recipes: React.FC<Props> = ({ isLoggedIn, isPremium }) => {
  const [tab, setTab] = useState<TabId>('my');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [categories, setCategories] = useState<RecipeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState<Recipe | null>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Reset page when tab changes
  useEffect(() => { setPage(1); setQuery(''); setSelectedCategory(null); }, [tab]);

  // Load categories once
  useEffect(() => {
    axios.get(`${API_URL}/api/recipe-categories?pagination[pageSize]=100`)
      .then(res => setCategories(res.data?.data || []))
      .catch(() => setCategories([]));
  }, []);

  // Load recipes based on active tab
  useEffect(() => {
    const jwt = localStorage.getItem('jwt');
    const userId = localStorage.getItem('userId');
    if (!jwt || !userId) { setLoading(false); setRecipes([]); setTotalCount(0); return; }

    setLoading(true);
    const headers = { Authorization: `Bearer ${jwt}` };

    if (tab === 'my') {
      // My custom recipes — from user-custom-recipe (explicit image/category populate to avoid Strapi v5 "image.related" 400)
      const params: Record<string, any> = {
        'pagination[page]': page,
        'pagination[pageSize]': PAGE_SIZE,
        'pagination[withCount]': true,
        'populate[image][fields][0]': 'url',
        'populate[image][fields][1]': 'formats',
        'populate[recipe_category][fields][0]': 'recipe_category_name',
        'filters[user][id][$eq]': userId,
        sort: 'createdAt:desc',
      };
      if (debouncedQuery.trim()) params['filters[title][$containsi]'] = debouncedQuery.trim();
      if (selectedCategory) params['filters[recipe_category][id][$eq]'] = selectedCategory;

      axios.get(`${API_URL}/api/user-custom-recipes`, { params, headers })
        .then(res => {
          setRecipes(res.data?.data || []);
          setTotalCount(res.data?.meta?.pagination?.total || 0);
        })
        .catch((err) => {
          console.error('Failed to load my recipes:', err?.response?.status, err?.response?.data);
          setRecipes([]); setTotalCount(0);
        })
        .finally(() => setLoading(false));
    } else if (tab === 'favorites' || tab === 'saved') {
      // Favorites + Saved both pull from /api/recipes using relation filters (matches Flutter app)
      const relationFilter = tab === 'favorites'
        ? 'filters[favorite_recipes][user][id][$eq]'
        : 'filters[user_customized_recipes][user][id][$eq]';

      const params: Record<string, any> = {
        'pagination[page]': page,
        'pagination[pageSize]': PAGE_SIZE,
        'pagination[withCount]': true,
        'fields[0]': 'title',
        'fields[1]': 'cooking_time',
        'fields[2]': 'servings',
        'fields[3]': 'thumbs_up_count',
        'fields[4]': 'thumbs_down_count',
        'populate[image][fields][0]': 'url',
        'populate[image][fields][1]': 'formats',
        'populate[recipe_subcategory][populate][recipe_category][fields][0]': 'recipe_category_name',
        [relationFilter]: userId,
        sort: 'createdAt:desc',
      };
      if (debouncedQuery.trim()) params['filters[title][$containsi]'] = debouncedQuery.trim();
      if (selectedCategory) params['filters[recipe_category][id][$eq]'] = selectedCategory;

      axios.get(`${API_URL}/api/recipes`, { params, headers })
        .then(res => {
          const list = (res.data?.data || []).map((r: any) => ({
            ...r,
            // Normalize recipe_category from recipe_subcategory.recipe_category for display
            recipe_category: r.recipe_category || r.recipe_subcategory?.recipe_category,
          }));
          setRecipes(list);
          setTotalCount(res.data?.meta?.pagination?.total || list.length);
        })
        .catch((err) => {
          console.error(`Failed to load ${tab} recipes:`, err?.response?.status, err?.response?.data);
          setRecipes([]); setTotalCount(0);
        })
        .finally(() => setLoading(false));
    }
  }, [tab, debouncedQuery, selectedCategory, page]);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selected ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selected]);

  // Load full details when selecting
  const openRecipe = async (recipe: Recipe) => {
    setSelected(recipe);
    try {
      const jwt = localStorage.getItem('jwt');
      const id = recipe.documentId || recipe.id;
      // My Recipes uses user-custom-recipe, others use the base recipe endpoint
      const endpoint = tab === 'my' ? 'user-custom-recipes' : 'recipes';
      const res = await axios.get(`${API_URL}/api/${endpoint}/${id}`, {
        params: {
          'populate[image][fields][0]': 'url',
          'populate[image][fields][1]': 'formats',
          'populate[recipe_category][fields][0]': 'recipe_category_name',
          'populate[nutrients]': true,
          'populate[ingredients]': true,
        },
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : undefined,
      });
      setSelected(res.data?.data || recipe);
    } catch {}
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const resetFilters = () => { setQuery(''); setSelectedCategory(null); setPage(1); };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>My Recipes — EatMeat</title>
        <meta name="description" content="Your personal collection of custom carnivore and ketovore recipes." />
      </Helmet>

      <style>{`
        @keyframes fadeSlide { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { 0% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes scaleIn { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes pulse-slow { 0%,100% { opacity: 0.3; } 50% { opacity: 0.55; } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shimmer-text { background: linear-gradient(90deg, #ef4444 0%, #f97316 30%, #ef4444 60%, #f97316 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: shimmer 6s linear infinite; }
        .skeleton-shimmer { background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%); background-size: 200% 100%; animation: shimmer 1.4s infinite linear; }
      `}</style>

      {/* ═══ Page header strip ═══ */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🍖</span>
            <div>
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">My Recipes</h1>
              <p className="text-gray-500 text-xs">Your custom recipes, favorites, and saved meals</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Logged-out marketing view ═══ */}
      {!isLoggedIn ? (
        <LoggedOutRecipesView />
      ) : !isPremium ? (
        <PremiumPaywallView />
      ) : (
      <>
      {/* ═══ Hero + search ═══ */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-red-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 9s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-20 -right-20 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-25" style={{ animation: 'pulse-slow 11s ease-in-out infinite 1s' }} />

        <div className="relative max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="text-center max-w-2xl mx-auto mb-6" style={{ animation: 'fadeSlide 0.7s ease-out' }}>
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-semibold text-gray-700 mb-4 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> {loading ? 'Loading…' : `${totalCount.toLocaleString()} ${totalCount === 1 ? 'recipe' : 'recipes'}`}
            </div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1] mb-3">
              {tab === 'my' && <>Your <span className="shimmer-text">custom</span> recipes</>}
              {tab === 'favorites' && <>Your <span className="shimmer-text">favorite</span> recipes</>}
              {tab === 'saved' && <>Your <span className="shimmer-text">saved</span> recipes</>}
            </h2>
            <p className="text-gray-500 text-base md:text-lg">
              {tab === 'my' && "Recipes you've created in the mobile app."}
              {tab === 'favorites' && "Recipes you've marked with ❤️."}
              {tab === 'saved' && "Recipes you've customized with your own ingredients."}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex justify-center mb-8" style={{ animation: 'fadeSlide 0.7s ease-out 0.05s backwards' }}>
            <div className="inline-flex bg-white rounded-full p-1 shadow-sm border border-gray-100">
              {[
                { id: 'my' as TabId, label: 'My Recipes', icon: '🍳' },
                { id: 'favorites' as TabId, label: 'Favorites', icon: '❤️' },
                { id: 'saved' as TabId, label: 'Saved', icon: '🔖' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 md:px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                    tab === t.id ? 'bg-gradient-to-r from-primary to-orange-500 text-white shadow-md' : 'text-gray-600 hover:text-primary'
                  }`}
                >
                  <span>{t.icon}</span>
                  <span className="hidden sm:inline">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto mb-6" style={{ animation: 'fadeSlide 0.7s ease-out 0.1s backwards' }}>
            <div className="relative">
              <FaSearchI className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                placeholder="Search recipes — e.g. ribeye, bone broth, eggs…"
                className="w-full bg-white border border-gray-200 rounded-2xl pl-12 pr-12 py-3.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary shadow-sm transition-all"
              />
              {query && (
                <button onClick={() => { setQuery(''); setPage(1); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors">
                  <FaTimesI className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Category chips */}
          <div className="flex gap-2 overflow-x-auto pb-2 max-w-4xl mx-auto justify-start md:justify-center flex-nowrap md:flex-wrap" style={{ animation: 'fadeSlide 0.7s ease-out 0.2s backwards' }}>
            <button
              onClick={() => { setSelectedCategory(null); setPage(1); }}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                !selectedCategory ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-primary hover:text-primary'
              }`}
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  selectedCategory === cat.id ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200 hover:border-primary hover:text-primary'
                }`}
              >
                {cat.recipe_category_name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Recipe grid ═══ */}
      <section className="relative max-w-7xl mx-auto px-4 md:px-6 pb-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="h-44 skeleton-shimmer" />
                <div className="p-4 space-y-2">
                  <div className="h-4 skeleton-shimmer rounded" />
                  <div className="h-3 w-2/3 skeleton-shimmer rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : recipes.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <div className="text-6xl mb-4 opacity-30">
              {tab === 'my' ? '🍳' : tab === 'favorites' ? '❤️' : '🔖'}
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {debouncedQuery || selectedCategory
                ? 'No recipes match'
                : tab === 'my'
                  ? "You haven't created any recipes yet"
                  : tab === 'favorites'
                    ? "No favorites yet"
                    : "No saved recipes yet"}
            </h3>
            <p className="text-gray-500 mb-6">
              {debouncedQuery || selectedCategory
                ? 'Try a different search or category.'
                : tab === 'my'
                  ? 'Create your first custom recipe in the EatMeat mobile app. It will appear here automatically.'
                  : tab === 'favorites'
                    ? 'Tap ❤️ on any recipe in the app to save it as a favorite.'
                    : 'Customize a recipe in the app (tweak ingredients or serving size) and it will show up here.'}
            </p>
            {debouncedQuery || selectedCategory ? (
              <button onClick={resetFilters} className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                Clear filters
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="https://apps.apple.com/app/id6746769046" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors">
                  Get iOS app
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat" target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-[#01875F] text-white rounded-xl font-semibold hover:bg-[#017550] transition-colors">
                  Get Android app
                </a>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {recipes.map((r, i) => (
                <RecipeCard key={r.id} recipe={r} delay={Math.min(i * 0.04, 0.5)} onClick={() => openRecipe(r)} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10">
                <button
                  onClick={() => { setPage(Math.max(1, page - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-semibold text-gray-700 hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  ‹ Prev
                </button>
                <span className="text-sm font-semibold text-gray-600 px-4">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => { setPage(Math.min(totalPages, page + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-semibold text-gray-700 hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next ›
                </button>
              </div>
            )}
          </>
        )}
      </section>
      </>
      )}

      {/* ═══ Detail modal ═══ */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
          style={{ animation: 'fadeIn 0.25s ease-out' }}
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white w-full md:max-w-3xl md:max-h-[90vh] rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            style={{ animation: 'scaleIn 0.3s ease-out', maxHeight: '92vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <RecipeDetail recipe={selected} onClose={() => setSelected(null)} canCustomize={!!isPremium} />
          </div>
        </div>
      )}
    </div>
  );
};

const RecipeCard: React.FC<{ recipe: Recipe; delay: number; onClick: () => void }> = ({ recipe, delay, onClick }) => {
  const { ref, inView } = useInView();
  const imgUrl = resolveImageUrl(recipe.image);
  const proteinN = recipe.nutrients?.find(n => n.nutrient_name?.toLowerCase().includes('protein'))?.value;

  return (
    <div
      ref={ref}
      onClick={onClick}
      className="group bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 transition-all duration-300 cursor-pointer"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s, box-shadow 0.3s, border-color 0.3s`,
      }}
    >
      <div className="relative h-40 md:h-44 bg-gradient-to-br from-orange-100 via-red-100 to-red-200 overflow-hidden">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={recipe.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl opacity-40">🍖</div>
        )}
        {recipe.recipe_category?.recipe_category_name && (
          <div className="absolute top-2 left-2 bg-white/90 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-bold text-gray-800 shadow-md">
            {recipe.recipe_category.recipe_category_name}
          </div>
        )}
        {(recipe.thumbs_up_count || 0) > 0 && (
          <div className="absolute top-2 right-2 bg-white/90 backdrop-blur rounded-full px-2 py-1 text-[10px] font-bold text-gray-800 shadow-md flex items-center gap-1">
            <FaThumbsUpI className="w-2.5 h-2.5 text-green-600" /> {recipe.thumbs_up_count}
          </div>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-bold text-sm md:text-base text-gray-900 mb-1.5 line-clamp-2 min-h-[2.5rem] group-hover:text-primary transition-colors">{recipe.title}</h3>
        {recipe.is_shared && (
          <div className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-50 rounded-full px-2 py-0.5 mb-2">
            🌐 Shared
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {recipe.cooking_time && (
            <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
              <FaClockI className="w-2.5 h-2.5" /> {recipe.cooking_time}
            </div>
          )}
          {(recipe.servings || 0) > 0 && (
            <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 bg-gray-50 rounded-full px-2 py-0.5">
              <FaUtensilsI className="w-2.5 h-2.5" /> {recipe.servings}
            </div>
          )}
          {proteinN && (
            <div className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 bg-red-50 rounded-full px-2 py-0.5">
              🥩 {proteinN}g
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RecipeDetail: React.FC<{ recipe: Recipe; onClose: () => void; canCustomize?: boolean }> = ({ recipe, onClose, canCustomize }) => {
  const imgUrl = resolveImageUrl(recipe.image);
  const [customizing, setCustomizing] = useState(false);
  const [customizeData, setCustomizeData] = useState<{
    detail: Recipe;
    existing: any | null;
  } | null>(null);
  const [loadingCustomize, setLoadingCustomize] = useState(false);
  const [customizeError, setCustomizeError] = useState<string | null>(null);

  const openCustomize = async () => {
    if (!canCustomize) return;
    setCustomizing(true);
    if (customizeData) return; // already loaded
    setLoadingCustomize(true);
    setCustomizeError(null);
    try {
      const token = localStorage.getItem('jwt') || '';
      const userIdStr = localStorage.getItem('userId') || '';
      const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
      const docId = recipe.documentId || recipe.id;
      // 1. Fetch the recipe with ingredients + their replaceableIngredients populated.
      const recipeRes = await axios.get(`${API_URL}/api/recipes/${docId}`, {
        params: {
          'populate[image][fields][0]': 'url',
          'populate[image][fields][1]': 'formats',
          'populate[recipe_category][fields][0]': 'recipe_category_name',
          'populate[nutrients]': true,
          'populate[ingredients][populate][replaceableIngredients][populate][IngredientName][fields][0]': 'name',
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const detail: Recipe = recipeRes.data?.data || recipeRes.data;

      // 2. Fetch existing user-customized-recipe for this user+recipe pair.
      let existing: any = null;
      if (userId) {
        try {
          const customRes = await axios.get(`${API_URL}/api/user-customized-recipes`, {
            params: {
              'filters[user][id][$eq]': userId,
              'filters[recipe][id][$eq]': detail.id,
              'pagination[pageSize]': 1,
            },
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          existing = customRes.data?.data?.[0] || null;
        } catch { /* no row */ }
      }

      setCustomizeData({ detail, existing });
    } catch (err: any) {
      setCustomizeError(err?.response?.data?.error?.message || 'Failed to load customization.');
    } finally {
      setLoadingCustomize(false);
    }
  };

  const closeCustomize = () => setCustomizing(false);

  return (
    <>
      {/* Hero image with close */}
      <div className="relative flex-shrink-0">
        <div className="relative h-56 md:h-72 bg-gradient-to-br from-orange-200 via-red-300 to-red-500 overflow-hidden">
          {imgUrl ? (
            <img src={imgUrl} alt={recipe.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-8xl opacity-30">🍖</div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent" />
        </div>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-700 hover:scale-110 transition-transform"
          aria-label="Close"
        >
          <FaTimesI className="w-4 h-4" />
        </button>
        <div className="absolute bottom-4 left-4 right-16 text-white">
          {recipe.recipe_category?.recipe_category_name && (
            <div className="inline-block bg-white/20 backdrop-blur rounded-full px-2.5 py-0.5 text-[10px] font-bold mb-2">
              {recipe.recipe_category.recipe_category_name}
            </div>
          )}
          <h2 className="text-xl md:text-2xl font-extrabold leading-tight drop-shadow-lg">{recipe.title}</h2>
          {recipe.is_shared && (
            <div className="inline-flex items-center gap-1 mt-2 bg-green-500/90 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-bold">
              🌐 Shared with community
            </div>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="overflow-y-auto p-5 md:p-6 flex-1">
        {/* Info strip */}
        <div className="flex flex-wrap gap-2 mb-5">
          {recipe.cooking_time && (
            <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 rounded-full px-3 py-1.5 text-xs font-bold">
              <FaClockI className="w-3 h-3" /> {recipe.cooking_time}
            </div>
          )}
          {(recipe.servings || 0) > 0 && (
            <div className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 rounded-full px-3 py-1.5 text-xs font-bold">
              <FaUtensilsI className="w-3 h-3" /> {recipe.servings} servings
            </div>
          )}
          {(recipe.thumbs_up_count || 0) > 0 && (
            <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1.5 text-xs font-bold">
              <FaThumbsUpI className="w-3 h-3" /> {recipe.thumbs_up_count}
            </div>
          )}
          {(recipe.thumbs_down_count || 0) > 0 && (
            <div className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-600 rounded-full px-3 py-1.5 text-xs font-bold">
              <FaThumbsDownI className="w-3 h-3" /> {recipe.thumbs_down_count}
            </div>
          )}
        </div>

        {/* Description */}
        {recipe.description && (
          <p className="text-gray-600 leading-relaxed mb-5">{recipe.description}</p>
        )}

        {/* Nutrients */}
        {recipe.nutrients && recipe.nutrients.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Nutrition per serving</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {recipe.nutrients.map((n, i) => (
                <div key={i} className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl p-3 text-center">
                  <div className="text-lg font-extrabold text-gray-900">{n.value}</div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mt-0.5">{n.nutrient_name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ingredients */}
        {recipe.ingredients && recipe.ingredients.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 rounded-lg bg-red-50 flex items-center justify-center text-sm">🥩</span>
                Ingredients
              </h3>
              {canCustomize && (
                <button onClick={customizing ? closeCustomize : openCustomize}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  {customizing ? 'Done customizing' : '✏️ Customize'}
                </button>
              )}
            </div>
            {customizing ? (
              <CustomizePanel
                baseRecipe={recipe}
                data={customizeData}
                loading={loadingCustomize}
                error={customizeError}
                onSaved={(record) => setCustomizeData((d) => d ? { ...d, existing: record } : d)}
                onReset={() => setCustomizeData((d) => d ? { ...d, existing: null } : d)}
              />
            ) : (
              <ul className="space-y-2">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg px-3 py-2.5">
                    <div className="w-5 h-5 rounded border-2 border-gray-300 bg-white flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-800 font-medium">{ing.IngredientName}</span>
                      {(ing.Quantity || ing.Unit) && (
                        <span className="text-xs text-gray-500 ml-1">
                          · {ing.Quantity} {ing.Unit}
                        </span>
                      )}
                      {ing.Comments && (
                        <span className="text-xs text-gray-400 italic ml-1">({ing.Comments})</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Instructions */}
        {recipe.instructions && (
          <div className="mb-4">
            <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 rounded-lg bg-orange-50 flex items-center justify-center text-sm">📝</span>
              Instructions
            </h3>
            <div className="bg-gray-50 rounded-xl p-4">
              {renderBlocks(recipe.instructions)}
            </div>
          </div>
        )}

        {/* CTA footer */}
        <div className="bg-gradient-to-br from-primary/5 to-orange-50 border border-primary/20 rounded-2xl p-4 flex items-center gap-3 mt-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <FaHeartI className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="font-bold text-gray-900 text-sm">Save to your meal plan</div>
            <div className="text-xs text-gray-500">Download EatMeat to track this recipe's macros.</div>
          </div>
          <a
            href="https://apps.apple.com/app/id6746769046"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-white font-semibold text-xs px-3 py-2 rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
          >
            Get app
          </a>
        </div>
      </div>
    </>
  );
};

/* ═══ Customize panel ═══ */

interface CustomizePanelProps {
  baseRecipe: Recipe;
  data: { detail: Recipe; existing: any | null } | null;
  loading: boolean;
  error: string | null;
  onSaved: (record: any) => void;
  onReset: () => void;
}

const CustomizePanel: React.FC<CustomizePanelProps> = ({ baseRecipe, data, loading, error, onSaved, onReset }) => {
  const detail = data?.detail || baseRecipe;
  const existing = data?.existing || null;

  // Local map of ingredient.id -> replacement.IngredientName.id (0 means "original")
  const [mods, setMods] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Initialize from existing customization (modified_ingredients JSON map)
  useEffect(() => {
    if (existing?.modified_ingredients && typeof existing.modified_ingredients === 'object') {
      const initial: Record<number, number> = {};
      for (const [k, v] of Object.entries(existing.modified_ingredients)) {
        const numK = parseInt(k, 10);
        const numV = typeof v === 'number' ? v : parseInt(String(v), 10);
        if (Number.isFinite(numK) && Number.isFinite(numV)) initial[numK] = numV;
      }
      setMods(initial);
    } else {
      setMods({});
    }
  }, [existing]);

  if (loading) {
    return <div className="text-sm text-gray-400 py-6 text-center">Loading customize options…</div>;
  }
  if (error) {
    return <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>;
  }

  const ingredients = detail.ingredients || [];

  const dirty = (() => {
    const orig = existing?.modified_ingredients || {};
    const allKeys: string[] = Array.from(new Set(Object.keys(orig).concat(Object.keys(mods))));
    for (const k of allKeys) {
      if ((orig as any)[k] !== mods[parseInt(k, 10)]) return true;
    }
    return false;
  })();

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const token = localStorage.getItem('jwt') || '';
      const userIdStr = localStorage.getItem('userId') || '';
      const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
      if (!token || !userId) throw new Error('Not logged in.');

      // Strapi expects { data: { ... } }
      const payload = {
        data: {
          user: userId,
          recipe: detail.id,
          modified_ingredients: mods,
        },
      };

      let saved;
      if (existing?.documentId || existing?.id) {
        const idToUpdate = existing.documentId || existing.id;
        const res = await axios.put(`${API_URL}/api/user-customized-recipes/${idToUpdate}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        saved = res.data?.data || res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/user-customized-recipes`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        saved = res.data?.data || res.data;
      }
      setMsg({ type: 'success', text: 'Customization saved.' });
      onSaved(saved);
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error?.message || e?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!existing?.documentId && !existing?.id) {
      setMods({});
      return;
    }
    if (!window.confirm('Remove all customizations for this recipe?')) return;
    setSaving(true); setMsg(null);
    try {
      const token = localStorage.getItem('jwt') || '';
      const idToDelete = existing.documentId || existing.id;
      await axios.delete(`${API_URL}/api/user-customized-recipes/${idToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMods({});
      onReset();
      setMsg({ type: 'success', text: 'Customizations cleared.' });
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.error?.message || 'Reset failed.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">
        Tap an ingredient to swap with one of its alternatives, or keep the original. Customizations are saved to your account.
      </p>

      {msg && (
        <div className={`px-3 py-2 rounded-lg text-xs mb-3 ${
          msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200'
                                 : 'bg-red-50 text-red-700 border border-red-200'
        }`}>{msg.text}</div>
      )}

      <ul className="space-y-2">
        {ingredients.map((ing) => {
          const ingId = ing.id ?? -1;
          const variants = (ing.replaceableIngredients || []).filter(Boolean);
          const hasVariants = variants.length > 0;
          const selectedRepId = mods[ingId] ?? 0;
          const selectedVariant = selectedRepId
            ? variants.find((v) => {
                const repName = v.IngredientName as any;
                return repName?.id === selectedRepId;
              })
            : null;
          const displayName = selectedVariant
            ? (typeof selectedVariant.IngredientName === 'object' ? (selectedVariant.IngredientName as any)?.name : selectedVariant.IngredientName) || ing.IngredientName
            : ing.IngredientName;

          return (
            <li key={ingId} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded border-2 border-gray-300 bg-white flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-800 font-semibold">
                    {displayName}
                    {(ing.Quantity || ing.Unit) && (
                      <span className="text-xs text-gray-500 font-normal ml-1">· {ing.Quantity} {ing.Unit}</span>
                    )}
                    {selectedVariant && (
                      <span className="ml-2 text-[10px] font-bold uppercase text-primary bg-primary/10 px-1.5 py-0.5 rounded">Modified</span>
                    )}
                  </div>
                  {!hasVariants ? (
                    <div className="text-[11px] text-gray-400 mt-1">No alternatives available</div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <button
                        type="button"
                        onClick={() => setMods((m) => { const c = { ...m }; delete c[ingId]; return c; })}
                        className={`text-[11px] px-2 py-1 rounded-full font-semibold transition-colors ${
                          !selectedRepId ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary hover:text-primary'
                        }`}>
                        Original
                      </button>
                      {variants.map((v) => {
                        const repName = v.IngredientName as any;
                        const repId = repName?.id;
                        const repLabel = repName?.name || (typeof repName === 'string' ? repName : '—');
                        if (!repId) return null;
                        const isSelected = selectedRepId === repId;
                        return (
                          <button
                            key={repId}
                            type="button"
                            onClick={() => setMods((m) => ({ ...m, [ingId]: repId }))}
                            className={`text-[11px] px-2 py-1 rounded-full font-semibold transition-colors ${
                              isSelected ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary hover:text-primary'
                            }`}>
                            {repLabel}
                            {v.Comment && <span className="ml-1 opacity-70">· {v.Comment}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-gray-100">
        <div className="text-[11px] text-gray-400">
          {Object.keys(mods).length} ingredient{Object.keys(mods).length === 1 ? '' : 's'} modified
        </div>
        <div className="flex gap-2">
          {existing && (
            <button onClick={handleReset} disabled={saving}
              className="text-xs px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-semibold disabled:opacity-50">
              Reset
            </button>
          )}
          <button onClick={handleSave} disabled={saving || !dirty}
            className="text-xs px-3 py-1.5 rounded-md bg-primary text-white hover:opacity-90 font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : existing ? 'Update' : 'Save customization'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Recipes;
